import api from "./apiClient";

/* ====================================================== */
/* ERROR NORMALIZER                                       */
/* ====================================================== */

function normalizeError(
  err,
  fallbackMessage =
    "Something went wrong"
) {

  const message =
    err?.response?.data
      ?.message ||
    err?.message ||
    fallbackMessage;

  const normalized =
    new Error(message);

  normalized.status =
    err?.response?.status;

  normalized.raw =
    err;

  return normalized;

}

/* ====================================================== */
/* SAFE ARRAY                                             */
/* ====================================================== */

function toArray(value) {

  return Array.isArray(
    value
  )
    ? value
    : [];

}

/* ====================================================== */
/* BILLING RESPONSE HELPERS                               */
/* ====================================================== */

/**
 * Extract the company object from supported API response formats.
 *
 * Supported:
 *   { company: {...} }
 *   { data: { company: {...} } } // if returned directly by an adapter
 *   {...company fields}
 *
 * Axios response.data is passed into this helper.
 */
function extractCompany(responseData) {
  if (
    !responseData ||
    typeof responseData !== "object" ||
    Array.isArray(responseData)
  ) {
    return null;
  }

  const company =
    responseData.company &&
    typeof responseData.company === "object" &&
    !Array.isArray(responseData.company)
      ? responseData.company
      : responseData.data &&
          typeof responseData.data === "object" &&
          !Array.isArray(responseData.data)
        ? responseData.data.company || responseData.data
        : responseData;

  if (
    !company ||
    typeof company !== "object" ||
    Array.isArray(company)
  ) {
    return null;
  }

  return company;
}

/**
 * Normalize a country code without replacing missing values
 * with a default country.
 *
 * GU remains GU.
 * IN remains IN.
 * Missing values remain empty.
 */
function normalizeCountryCode(value) {
  if (
    value === undefined ||
    value === null
  ) {
    return "";
  }

  const country = String(value)
    .trim()
    .toUpperCase();

  if (!country) {
    return "";
  }

  if (country === "IND" || country === "INDIA") {
    return "IN";
  }

  return country;
}

/**
 * Return a company object with a normalized address country.
 *
 * Does not mutate the original API response.
 */
function normalizeBillingCompany(company) {
  if (
    !company ||
    typeof company !== "object"
  ) {
    return null;
  }

  const address =
    company.address &&
    typeof company.address === "object" &&
    !Array.isArray(company.address)
      ? company.address
      : {};

  return {
    ...company,

    address: {
      ...address,

      country: normalizeCountryCode(
        address.country
      ),
    },
  };
}

/* ====================================================== */
/* COMPANY BILLING INFO                                   */
/* ====================================================== */

/**
 * Fetch company billing information.
 *
 * Important:
 * - Preserves the saved country code.
 * - Does not silently default a missing country to IN.
 * - Supports common company API response structures.
 * - Returns a normalized company object.
 */
export const getBillingInfo = async (companyId) => {
  try {
    if (
      typeof companyId !== "string" ||
      !companyId.trim()
    ) {
      throw new Error(
        "A valid company ID is required"
      );
    }

    const response = await api.get(
      `/company/${encodeURIComponent(companyId.trim())}`
    );

    const company = extractCompany(
      response?.data
    );

    if (!company) {
      const error = new Error(
        "The server returned an invalid company response"
      );

      error.code = "INVALID_COMPANY_RESPONSE";

      throw error;
    }

    return normalizeBillingCompany(company);
  } catch (err) {
    throw normalizeError(
      err,
      "Failed to fetch billing information"
    );
  }
};

/* ====================================================== */
/* UPDATE BILLING INFO                                    */
/* ====================================================== */

/**
 * Update company billing information.
 *
 * Important:
 * - Does not silently replace missing country with IN.
 * - Normalizes explicitly provided country codes.
 * - Does not mutate the caller's payload.
 * - Returns the saved company when the server provides it.
 */
export const updateBillingInfo = async (
  companyId,
  payload = {}
) => {
  try {
    if (
      typeof companyId !== "string" ||
      !companyId.trim()
    ) {
      throw new Error(
        "A valid company ID is required"
      );
    }

    if (
      !payload ||
      typeof payload !== "object" ||
      Array.isArray(payload)
    ) {
      throw new Error(
        "Invalid billing payload"
      );
    }

    const normalizedPayload = {
      ...payload,
    };

    /* ---------------------------------------------- */
    /* ADDRESS VALIDATION AND NORMALIZATION           */
    /* ---------------------------------------------- */

    if (
      Object.prototype.hasOwnProperty.call(
        payload,
        "address"
      )
    ) {
      if (
        !payload.address ||
        typeof payload.address !== "object" ||
        Array.isArray(payload.address)
      ) {
        throw new Error(
          "Billing address must be an object"
        );
      }

      normalizedPayload.address = {
        ...payload.address,
      };

      if (
        Object.prototype.hasOwnProperty.call(
          payload.address,
          "country"
        )
      ) {
        const country = normalizeCountryCode(
          payload.address.country
        );

        if (country && !/^[A-Z]{2}$/.test(country)) {
          throw new Error(
            "Country must be a valid two-letter country code"
          );
        }

        normalizedPayload.address.country =
          country;
      }
    }

    /* ---------------------------------------------- */
    /* REQUEST                                        */
    /* ---------------------------------------------- */

    const response = await api.patch(
      `/company/${encodeURIComponent(companyId.trim())}/billing`,
      normalizedPayload
    );

    const responseData = response?.data;

    if (
      !responseData ||
      typeof responseData !== "object" ||
      Array.isArray(responseData)
    ) {
      const error = new Error(
        "The server returned an invalid billing update response"
      );

      error.code = "INVALID_BILLING_RESPONSE";

      throw error;
    }

    /* ---------------------------------------------- */
    /* RESPONSE NORMALIZATION                         */
    /* ---------------------------------------------- */

    const company = extractCompany(
      responseData
    );

    if (company) {
      return normalizeBillingCompany(company);
    }

    /*
     * Some endpoints return success metadata rather than
     * the updated company. Preserve that response rather
     * than fabricating company fields.
     */
    return responseData;
  } catch (err) {
    throw normalizeError(
      err,
      "Failed to update billing information"
    );
  }
};

/* ====================================================== */
/* GET INVOICES                                           */
/* ====================================================== */

export const getInvoices =
  async () => {

    try {

      const response =
        await api.get(
          "/invoices"
        );

      return toArray(
        response?.data
          ?.invoices ||
          response?.data
      );

    } catch (err) {

      throw normalizeError(
        err,
        "Failed to fetch invoices"
      );

    }

  };

/* ====================================================== */
/* LAST INVOICE                                           */
/* ====================================================== */

export const getLastInvoiceSummary =
  async () => {

    try {

      const response =
        await api.get(
          "/invoices/summary/last"
        );

      return (
        response?.data
          ?.lastInvoice ||
        null
      );

    } catch (err) {

      throw normalizeError(
        err,
        "Failed to fetch last invoice summary"
      );

    }

  };

/* ====================================================== */
/* GENERATE INVOICE                                       */
/* ====================================================== */

export const generateInvoice =
  async (plan) => {

    try {

      if (
        ![
          "pro",
          "enterprise",
        ].includes(plan)
      ) {
        throw new Error(
          "Valid plan is required"
        );
      }

      const response =
        await api.post(
          "/invoices/create",
          {
            plan,
          }
        );

      return {

        success:
          response?.data
            ?.success ??
          true,

        invoice:
          response?.data
            ?.invoice ||
          null,

        billing:
          response?.data
            ?.billing ||
          null,

        skipped:
          response?.data
            ?.skipped ||
          false,

        message:
          response?.data
            ?.message ||
          "Invoice generated successfully",

      };

    } catch (err) {

      throw normalizeError(
        err,
        "Failed to generate invoice"
      );

    }

  };

/* ====================================================== */
/* DOWNLOAD INVOICE                                       */
/* ====================================================== */

export const downloadInvoice =
  async (
    invoiceId
  ) => {

    try {

      if (!invoiceId) {
        throw new Error(
          "Invoice ID is required"
        );
      }

      const response =
        await api.get(
          `/invoices/${invoiceId}/pdf`,
          {
            responseType:
              "blob",
          }
        );

      const blob =
        new Blob(
          [response.data],
          {
            type:
              "application/pdf",
          }
        );

      const url =
        window.URL.createObjectURL(
          blob
        );

      const anchor =
        document.createElement(
          "a"
        );

      anchor.href =
        url;

      anchor.download =
        `invoice_${invoiceId}.pdf`;

      document.body.appendChild(
        anchor
      );

      anchor.click();

      anchor.remove();

      window.URL.revokeObjectURL(
        url
      );

      return {
        success: true,
      };

    } catch (err) {

      throw normalizeError(
        err,
        "Failed to download invoice"
      );

    }

  };

/* ====================================================== */
/* RETRY INVOICE SYNC                                     */
/* ====================================================== */

export const retryInvoiceSync =
  async (
    invoiceId
  ) => {

    try {

      if (!invoiceId) {
        throw new Error(
          "Invoice ID is required"
        );
      }

      const response =
        await api.post(
          `/invoices/${invoiceId}/retry-sync`
        );

      return {
        success:
          response?.data
            ?.success ??
          true,

        message:
          response?.data
            ?.message ||
          "Invoice sync retry initiated",
      };

    } catch (err) {

      throw normalizeError(
        err,
        "Failed to retry invoice sync"
      );

    }

  };

/* ====================================================== */
/* CREATE ORDER                                            */
/* ====================================================== */
/**
 * IMPORTANT:
 * Backend controls:
 * - pricing
 * - discounts
 * - validation
 *
 * Frontend NEVER sends amount.
 */

export const createOrder =
  async ({
    plan,
    promoCode,
  }) => {

    try {

      if (
        ![
          "pro",
          "enterprise",
        ].includes(plan)
      ) {
        throw new Error(
          "Valid plan is required"
        );
      }

      const response =
        await api.post(
          "/billing/create-order",
          {
            plan,

            ...(promoCode
              ? {
                  promoCode,
                }
              : {}),
          }
        );

      const data =
        response?.data;

      /* ---------------------------------------------- */
      /* VALIDATION                                     */
      /* ---------------------------------------------- */

      if (
        !data?.success
      ) {
        throw new Error(
          data?.message ||
            "Failed to create order"
        );
      }

      if (
        !data?.order?.id
      ) {

        console.error(
          "[billingService.createOrder] Invalid order response:",
          data
        );

        throw new Error(
          "Invalid payment order received"
        );

      }

      /* ---------------------------------------------- */
      /* NORMALIZED RESPONSE                            */
      /* ---------------------------------------------- */

      return {

        success: true,

        order: {
          id:
            data.order.id,

          amount:
            data.order.amount,

          currency:
            data.order.currency,

          receipt:
            data.order.receipt,

          status:
            data.order.status,
        },

        pricing:
          data.pricing ||
          null,

        billing:
          data.billing ||
          null,

      };

    } catch (err) {

      throw normalizeError(
        err,
        "Failed to create payment order"
      );

    }

  };

/* ====================================================== */
/* EXPORT                                                 */
/* ====================================================== */

const billingService = {

  /* company */
  getBillingInfo,
  updateBillingInfo,

  /* invoices */
  getInvoices,
  getLastInvoiceSummary,
  generateInvoice,
  downloadInvoice,
  retryInvoiceSync,

  /* payments */
  createOrder,

};

export default billingService;